import { useMemo, type JSX } from 'react';
import {
  CREATE_NEW,
  type PersonChangeSet,
  type PersonnelDiff,
  type SameParishMatch,
} from '../../api-client';
import { RegistrarContact } from '../access/RegistrarContact';

/** The notice for a match in another parish — never a choice (cca-0x72d). */
const OTHER_PARISH_NOTICE =
  "They may already be on file at another parish — once you've submitted, ask the Registrar to link the two records.";

/** What happens to a new person whose only matches are elsewhere: they are created here. */
const SAVED_AS_NEW = 'Saved here as a new contact.';

const sameParishMatches = (c: PersonChangeSet): SameParishMatch[] =>
  c.potentialMatches.filter((m): m is SameParishMatch => m.same_parish);

/** A new person the editor must answer for: they match someone at THIS parish. */
const needsAnswer = (c: PersonChangeSet): boolean => c.isNew && sameParishMatches(c).length > 0;

interface PersonnelDiffPreviewProps {
  diff: PersonnelDiff;
  mergeChoices: Record<string, string>;
  onChoice: (key: string, choice: string) => void;
}

export function PersonnelDiffPreview({
  diff,
  mergeChoices,
  onChoice,
}: PersonnelDiffPreviewProps): JSX.Element {
  const noChanges = useMemo(
    () =>
      diff.seniorMinisterEdits.length === 0 &&
      diff.changes.every(
        (c) =>
          c.rolesAdded.length === 0 && c.rolesRemoved.length === 0 && c.fieldEdits.length === 0,
      ),
    [diff],
  );

  // Only a SAME-parish match asks the editor to choose. A new person whose only
  // matches are elsewhere is listed with the other changes and a notice, and is
  // created as a new Contact here.
  const matchChanges = diff.changes.filter(needsAnswer);
  const otherChanges = diff.changes.filter((c) => !needsAnswer(c));

  return (
    <section className="space-y-4 rounded-card border border-ccaanz-gold/30 bg-ccaanz-gold-light/40 p-5">
      <h3 className="section-heading">Review changes before submitting</h3>

      {matchChanges.length > 0 && (
        <div className="rounded-control border border-ccaanz-amber/40 bg-ccaanz-amber-light px-4 py-3">
          <p className="font-medium text-ccaanz-amber">Possible existing contacts found</p>
          <p className="mt-1 text-sm text-ccaanz-amber/90">
            For each person in this box, please confirm whether they're an existing contact or
            someone new.
          </p>
          <div className="mt-3 space-y-3">
            {matchChanges.map((c) => (
              <MatchResolver
                key={c.key}
                change={c}
                choice={mergeChoices[c.key]}
                onChoice={(v) => onChoice(c.key, v)}
              />
            ))}
          </div>
        </div>
      )}

      {noChanges ? (
        <p className="text-sm text-ccaanz-grey">No changes detected.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {diff.seniorMinisterEdits.length > 0 && (
            <li>
              <strong>Senior Minister:</strong>{' '}
              {diff.seniorMinisterEdits.map((e) => e.field).join(', ')} updated. The Registrar will
              be emailed.
            </li>
          )}
          {otherChanges.map((c) => (
            <li key={c.key}>
              <strong>{c.name || c.email}</strong>
              {c.isNew && (
                <>
                  {' '}
                  · <em>new contact</em>
                </>
              )}
              {c.rolesAdded.length > 0 && <> · adds: {c.rolesAdded.join(', ')}</>}
              {c.rolesRemoved.length > 0 && <> · removes: {c.rolesRemoved.join(', ')}</>}
              {c.fieldEdits.length > 0 && (
                <> · edits: {c.fieldEdits.map((e) => e.field).join(', ')}</>
              )}
              {c.isNew && c.potentialMatches.some((m) => !m.same_parish) && (
                <OtherParishNotice savedAsNew className="mt-1" />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A choice row: the whole label is the touch target, 44px tall at least with
 * its content centred (MOB-8). A literal 44px, not `min-h-11` — this portal's
 * root font is 14px, so `min-h-11` (2.75rem) would be 38.5px.
 */
const CHOICE_ROW =
  'flex min-h-[44px] cursor-pointer items-center gap-2 rounded-control border border-brand-border p-2 hover:bg-brand-bg/30';

/**
 * One person's choices, as a radio group a screen reader names: a fieldset
 * whose legend is the "You entered: …" line (Miranda r1 S4, cca-0x72d). The
 * fieldset's border, padding and margin are reset so it looks as the plain
 * block did, and `min-w-0` stops a long email widening it past a phone's
 * screen; the legend may break anywhere, so that email wraps inside it
 * (Miranda r2). A match in another parish is a notice under the legend, never
 * among the choices.
 */
function MatchResolver({
  change,
  choice,
  onChoice,
}: {
  change: PersonChangeSet;
  choice: string | undefined;
  onChoice: (value: string) => void;
}): JSX.Element {
  return (
    <div className="rounded-control border border-ccaanz-amber-light bg-ccaanz-amber-light/40 p-3">
      <fieldset className="m-0 min-w-0 border-0 p-0">
        <legend className="p-0 font-medium [overflow-wrap:anywhere]">
          You entered: <span className="font-semibold">{change.name || '(no name)'}</span>
          {change.email && <> · {change.email}</>}
          {change.rolesAdded.length > 0 && <> · {change.rolesAdded.join(', ')}</>}
        </legend>
        {change.potentialMatches.some((m) => !m.same_parish) && (
          <OtherParishNotice savedAsNew={false} className="mt-1" />
        )}
        <div className="mt-3 space-y-2">
          {sameParishMatches(change).map((m) => (
            <label key={m.id} className={CHOICE_ROW}>
              <input
                type="radio"
                name={`merge-${change.key}`}
                value={m.id}
                checked={choice === m.id}
                onChange={(e) => onChoice(e.target.value)}
              />
              <span className="text-sm">
                <strong>Use existing:</strong> {m.full_name}
                {m.email && <> · {m.email}</>}
                {m.phone && <> · {m.phone}</>}
                {m.account_name && <> · {m.account_name}</>}
              </span>
            </label>
          ))}
          <label className={CHOICE_ROW}>
            <input
              type="radio"
              name={`merge-${change.key}`}
              value={CREATE_NEW}
              checked={choice === CREATE_NEW}
              onChange={(e) => onChoice(e.target.value)}
            />
            <span className="text-sm">
              <strong>Create new contact</strong> — this person isn't any of the above
            </span>
          </label>
        </div>
      </fieldset>
    </div>
  );
}

/**
 * A new person who may already be on file in another parish, and how to reach
 * the Registrar about it. `savedAsNew` adds what happens to them here: true in
 * the change list, where they can only be created. In a resolver it is false —
 * there the editor's choice decides, and "saved as a new contact" would be
 * untrue of "Use existing".
 */
function OtherParishNotice({
  savedAsNew,
  className = '',
}: {
  savedAsNew: boolean;
  className?: string;
}): JSX.Element {
  return (
    <div className={`text-sm text-ccaanz-amber ${className}`}>
      <p>{savedAsNew ? `${SAVED_AS_NEW} ${OTHER_PARISH_NOTICE}` : OTHER_PARISH_NOTICE}</p>
      <RegistrarContact className="mt-0.5" />
    </div>
  );
}

/** True when every new person with a SAME-parish match has an answer (link, or create new). */
export function allMatchesResolved(diff: PersonnelDiff, choices: Record<string, string>): boolean {
  return diff.changes.filter(needsAnswer).every((c) => Boolean(choices[c.key]));
}
